import { DotsVerticalIcon, Trash01Icon } from '@langchain/untitled-ui-icons';
import { CheckCircleIcon } from '@langchain/untitled-ui-icons';
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { formatDate } from '@/Pages/InsightsJobs/utils';
import { DataGrid, DataGridPagination } from '@/components/DataGrid';
import { useDataGridSizingLocalStorage } from '@/components/DataGrid.utils';
import { DeleteModal } from '@/components/Delete';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/Dropdown';
import { FiltersDisplay } from '@/components/FiltersDisplay/FiltersDisplay';
import { RunStatusIcon } from '@/components/RunStatus';
import { TableCellTextOverflow } from '@/components/Table';
import { Text } from '@/design-system';
import { useDataGridState } from '@/hooks/useDataGridState';
import { GridPaginationModel } from '@/hooks/useDataGridState';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { useOrganizationId } from '@/hooks/useSwr';
import { RunSchema, SessionClusterJob } from '@/types/schema';
import { apiSessionsPath } from '@/utils/constants';

import { useSessionClusterJobs } from '../ClusterJob/hooks/useSessionClusterJobs';
import { InsightsJobCategories } from '../InsightsJob/InsightsJobCategories';
import { mapStatus } from '../InsightsJobConfig/utils/mapStatus';
import { StatsSidebarLayout } from '../Project/Project';
import { TopLevelGroupsBreakdown } from './components/TopLevelGroupsBreakdown';

const columnHelper = createColumnHelper<SessionClusterJob>();

export type ClustersColumnsDefProps = {
  organizationId: string | null;
};

type RowData = SessionClusterJob[] & {
  headers?: Record<string, string | number> | undefined;
};

interface UseClustersTableProps {
  rows?: RowData;
  paginationModel: GridPaginationModel;
  onPaginationChange: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
  columns: ColumnDef<SessionClusterJob>[];
  setDeleteOpen?: React.Dispatch<React.SetStateAction<string | null>>;
  sessionId?: string;
  totalCount: number;
}

const useClustersTable = ({
  rows,
  paginationModel,
  onPaginationChange,
  columns,
  setDeleteOpen,
  sessionId,
  totalCount,
}: UseClustersTableProps) => {
  'use no memo';
  const EMPTY_TABLE = [];

  const [columnSizing, setColumnSizing] =
    useDataGridSizingLocalStorage('clusters');

  return useReactTable({
    columns: columns,
    data: rows ?? EMPTY_TABLE,
    state: {
      pagination: paginationModel,
      columnSizing,
    },
    meta: {
      actions: {
        handleDelete: setDeleteOpen,
      },
      sessionId,
      totalCount,
    },
    onPaginationChange,
    onColumnSizingChange: setColumnSizing,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualExpanding: true,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    columnResizeMode: 'onChange',
    pageCount: Math.ceil(totalCount / paginationModel.pageSize),
  });
};

const getClusterActionColumn = (): ColumnDef<SessionClusterJob> => {
  return columnHelper.display({
    id: 'menu_actions',
    cell: ({ row, table }) => {
      const { actions } = (table.options.meta ?? {}) as {
        actions: {
          handleDelete: (id: string) => void;
        };
      };

      return (
        <TableCellTextOverflow className="justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger
              onClick={(e) => e.stopPropagation()}
              className="flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-secondary focus:outline-none"
            >
              <DotsVerticalIcon className="h-5 w-5" />
            </DropdownMenuTrigger>

            <DropdownMenuContent
              className="z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                onClick={() => actions.handleDelete?.(row.original.id)}
                className="flex items-center gap-2"
              >
                <Trash01Icon className="h-5 w-5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCellTextOverflow>
      );
    },
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    size: 40,
  });
};

const getClustersColumnsDef: (
  props: ClustersColumnsDefProps
) => ColumnDef<SessionClusterJob>[] = () => [
  columnHelper.display({
    id: 'status',
    header: () => (
      <div className="pl-4">
        <CheckCircleIcon className="m-0.5 h-5 w-5" />
      </div>
    ),
    enableResizing: true,
    enableSorting: false,
    cell: ({ row }) => {
      const cluster = row.original;
      const mappedStatus = mapStatus(cluster.status);
      const runLikeObject = {
        status: mappedStatus,
        start_time: cluster.start_time,
        end_time: cluster.end_time,
      } as Pick<RunSchema, 'status' | 'start_time' | 'end_time'>;
      return (
        <div
          className="m-0 flex items-center justify-center p-0 pl-4"
          style={{ minWidth: 0, width: '100%' }}
        >
          <RunStatusIcon run={runLikeObject as RunSchema} />
        </div>
      );
    },
    size: 44,
    minSize: 44,
    maxSize: 44,
    meta: { style: { paddingLeft: 0, paddingRight: 0, margin: 0 } },
  }),
  columnHelper.display({
    id: 'name',
    header: 'Name',
    enableResizing: true,
    enableSorting: false,
    cell: ({ row }) => {
      const name = row.original.name;
      return (
        <Text variant="md" weight="medium">
          {name}
        </Text>
      );
    },
    size: 140,
    minSize: 120,
    maxSize: 180,
  }),
  columnHelper.display({
    id: 'clusters',
    header: 'Categories',
    enableResizing: true,
    enableSorting: false,
    cell: ({ row }) => <TopLevelGroupsBreakdown shape={row.original.shape} />,
    size: 160,
    minSize: 160,
    maxSize: 200,
  }),
  columnHelper.display({
    id: 'run_count',
    header: 'Run Count',
    enableResizing: true,
    enableSorting: false,
    cell: ({ row }) => {
      const shape = row.original.shape;
      if (!shape || shape.length === 0) {
        return (
          <Text variant="xs" color="tertiary">
            —
          </Text>
        );
      }
      // Last element in shape array represents sampled runs
      const runCount = shape.reduce((a, b) => a + b, 0);
      return (
        runCount != null && (
          <div
            data-testid="run-count-chip"
            className="inline-flex h-5 items-center justify-center rounded bg-tertiary px-1.5"
          >
            <div className="text-center text-xs font-medium leading-none">
              {runCount.toLocaleString()}
            </div>
          </div>
        )
      );
    },
    size: 80,
    minSize: 60,
    maxSize: 100,
  }),
  columnHelper.display({
    id: 'time_range',
    header: 'Time Range',
    enableResizing: true,
    enableSorting: false,
    cell: ({ row }) => {
      const startTime = row.original.start_time;
      const endTime = row.original.end_time;
      return (
        <Text variant="sm" color="tertiary">
          {startTime ? formatDate(startTime) : '-'} –{' '}
          {endTime ? formatDate(endTime) : '-'}
        </Text>
      );
    },
    size: 160,
    minSize: 120,
    maxSize: 200,
  }),
  columnHelper.display({
    id: 'filters',
    header: 'Filters',
    enableResizing: true,
    enableSorting: false,
    cell: ({ row }) => {
      const filters = row.original.metadata?.filter as string;
      return <FiltersDisplay filter={filters} />;
    },
    size: 120,
    minSize: 80,
    maxSize: 140,
  }),
  getClusterActionColumn(),
];

export const InsightsJobsTable = (props: {
  sessionId: string;
  onRowClick: (row: SessionClusterJob, event: React.MouseEvent) => void;
  tabIndex?: string;
  selectedClusterId?: string | null;
  selectedClusterJobId?: string | null;
  onClusterDeleted?: () => void;
}) => {
  const [, setSearchParams] = useSearchParams();
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const { paginationModel, setPaginationModel } = useDataGridState({});
  const [deleteOpen, setDeleteOpen] = useState<string | null>(null);

  const clioEnabled = useOrgConfig(OrgConfigs.clio_enabled).value;
  const shouldFetch = clioEnabled;

  const { data, isLoading, error, mutate } = useSessionClusterJobs(
    shouldFetch
      ? {
          session_id: props.sessionId,
          limit: paginationModel.pageSize,
          offset: paginationModel.pageIndex * paginationModel.pageSize,
        }
      : null
  );

  const rawClusters = data?.clustering_jobs || data;
  const clusters = useMemo(() => {
    if (!rawClusters || !Array.isArray(rawClusters)) return [];
    return rawClusters;
  }, [rawClusters]);

  useEffect(() => {
    return () => {
      setSearchParamsRef.current((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('clusterJobId');
        newParams.delete('clusterId');
        newParams.delete('selectedLevel');
        return newParams;
      });
    };
  }, []);

  const hasMoreData = clusters.length === paginationModel.pageSize;
  const currentlyLoadedCount =
    paginationModel.pageIndex * paginationModel.pageSize + clusters.length;
  const totalCount = hasMoreData
    ? currentlyLoadedCount + 1
    : currentlyLoadedCount;

  const organizationId = useOrganizationId();

  const columns = useMemo(() => {
    return getClustersColumnsDef({
      organizationId,
    });
  }, [organizationId]);

  const table = useClustersTable({
    rows: clusters,
    paginationModel,
    onPaginationChange: setPaginationModel,
    columns,
    setDeleteOpen,
    sessionId: props.sessionId,
    totalCount,
  });

  // Error state
  if (error) {
    return (
      <div className="space-y-4">
        <div className="overflow-hidden rounded-lg border border-secondary">
          <div className="p-8 text-center">
            <div className="mb-2 text-red-600">Error loading insights jobs</div>
            <div className="text-sm text-tertiary">{error.message}</div>
          </div>
        </div>
      </div>
    );
  }

  // Feature not enabled state
  if (!clioEnabled) {
    return (
      <div className="space-y-4">
        <div className="overflow-hidden rounded-lg border border-secondary">
          <div className="p-8 text-center">
            <div className="text-tertiary">Insights feature is not enabled</div>
          </div>
        </div>
      </div>
    );
  }

  if (props.tabIndex === '4' && props.selectedClusterJobId) {
    return (
      <StatsSidebarLayout automate={false}>
        <InsightsJobCategories />
      </StatsSidebarLayout>
    );
  }

  return (
    <div className="mb-4 space-y-4">
      <div className="my-4 flex flex-col gap-2 px-4">
        <Text variant="body">
          Use Insights to understand usage patterns and failure modes across
          runs. Kick off a new Insights Job by hitting +New in the top right.
          Jobs can take up to 15 minutes to complete.
        </Text>
        <Text variant="body" color="tertiary">
          Insights is in Beta and under active development. Please reach out to
          the LangSmith team with feedback — we'd love to hear from you!
        </Text>
      </div>
      <div className="overflow-hidden border-b border-secondary">
        <DataGrid
          table={table}
          cellHeight={56}
          isLoading={isLoading}
          emptyState={
            <Text variant="sm" color="tertiary">
              There are no cluster jobs yet
            </Text>
          }
          onClick={(row, event) => props.onRowClick(row, event)}
        />
      </div>

      <div className="mx-4 justify-end">
        <DataGridPagination table={table} />
      </div>

      <DeleteModal
        endpoint={`${apiSessionsPath}/${props.sessionId}/insights`}
        id={deleteOpen as string}
        isOpen={!!deleteOpen}
        doClose={() => {
          mutate(); // Refetch data using SWR
          props.onClusterDeleted?.();
          setDeleteOpen(null);
        }}
        invalidationPrefixes={[
          `${apiSessionsPath}/${props.sessionId}/insights`,
        ]}
        modalText="Are you sure you want to delete this cluster job?"
      />
    </div>
  );
};
